import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Plane, Route as RouteIcon, ClipboardList, Plus, ArrowRight,
  Navigation, Gauge, UserRound,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, to, cta }) => (
  <div className="glass hud-card group relative overflow-hidden rounded-2xl p-6 transition-all duration-200 hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,229,255,0.15)]">
    <div className="flex items-start justify-between">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-500/25 bg-cyan-500/10">
        <Icon className="h-5 w-5 text-cyan-400" />
      </div>
      <span className="font-mono-data text-3xl font-bold text-slate-100">{value}</span>
    </div>
    <div className="mt-4 text-xs uppercase tracking-wider text-slate-400">{label}</div>
    <Link to={to} className="mt-3 flex items-center gap-1 text-sm font-medium text-cyan-400 hover:text-cyan-300">
      {cta} <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ pilots: 0, aircraft: 0, routes: 0, briefings: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, r, b, p] = await Promise.all([
        api.get("/aircraft"),
        api.get("/routes"),
        api.get("/briefings"),
        api.get("/pilots"),
      ]);
      setCounts({ pilots: p.data.length, aircraft: a.data.length, routes: r.data.length, briefings: b.data.length });
      setRecent(b.data.slice(0, 5));
    })();
  }, []);

  return (
    <div className="space-y-8">
      <div className="animate-fade-up flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cyan-500/80">
            <Gauge className="h-3.5 w-3.5" /> Tableau de bord
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
            Bienvenue, {user?.name}
          </h1>
          <p className="mt-1 text-sm text-slate-400">Préparez votre navigation VFR de A à Z.</p>
        </div>
        <Link to="/briefings">
          <Button data-testid="dashboard-new-briefing" className="bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
            <Plus className="mr-1.5 h-4 w-4" /> Nouveau briefing
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
        <StatCard icon={UserRound} label="Pilotes" value={counts.pilots} to="/pilots" cta="Gérer les pilotes" />
        <StatCard icon={Plane} label="Avions enregistrés" value={counts.aircraft} to="/aircraft" cta="Gérer les avions" />
        <StatCard icon={RouteIcon} label="Routes sauvegardées" value={counts.routes} to="/routes" cta="Gérer les routes" />
        <StatCard icon={ClipboardList} label="Briefings" value={counts.briefings} to="/briefings" cta="Voir les briefings" />
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="glass rounded-2xl p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-slate-100">Briefings récents</h2>
            <Link to="/briefings" className="text-sm text-cyan-400 hover:text-cyan-300">Tout voir</Link>
          </div>
          {recent.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-700 py-12 text-center">
              <Navigation className="mx-auto mb-3 h-8 w-8 text-slate-600" />
              <p className="text-sm text-slate-400">Aucun briefing pour l'instant.</p>
              <Link to="/briefings" className="mt-2 inline-block text-sm text-cyan-400 hover:text-cyan-300">
                Créer votre premier briefing
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-slate-800">
              {recent.map((b) => (
                <li key={b.id}>
                  <Link to={`/briefings/${b.id}`} data-testid={`dashboard-briefing-${b.id}`}
                    className="flex items-center justify-between py-3 transition-colors hover:text-cyan-300">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="h-4 w-4 text-cyan-500" />
                      <span className="font-medium text-slate-200">{b.name}</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-500" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
